import { BehaviorSubject } from "rxjs";
import { AuthService } from "../../app/auth/auth.service";
import { IWebSocketService } from "./websocket.service.interface";

export abstract class BaseWebSocketService<T> implements IWebSocketService<T> {
  protected sockets: Map<string, WebSocket> = new Map();
  protected reconnectAttempts: Map<string, number> = new Map();
  protected readonly maxReconnectAttempts = 5;
  protected readonly reconnectDelay = 3000;

  protected connectionStatusSubject = new BehaviorSubject<
    "connecting" | "connected" | "disconnected" | "failed"
  >("disconnected");
  protected messagesSubject = new BehaviorSubject<T>({} as T);
  protected countNotificationsSubject = new BehaviorSubject<number>(0);
  protected setNotificationsCountSubject = new BehaviorSubject<number>(0);

  connectionStatus$ = this.connectionStatusSubject.asObservable();
  messages$ = this.messagesSubject.asObservable();
  countNotifications$ = this.countNotificationsSubject.asObservable();
  setNotificationsCount$ = this.setNotificationsCountSubject.asObservable();

  abstract appSyncDomain: string;
  abstract namespace: string;

  protected constructor(private authService: AuthService) {}

  connect(channel: string): void {
    if (
      this.sockets.has(channel) &&
      this.sockets.get(channel)?.readyState === WebSocket.OPEN
    ) {
      console.log(
        `WebSocket already connected to channel:  ${this.namespace}/${channel}`
      );
      return;
    }

    if (
      this.sockets.has(channel) &&
      this.sockets.get(channel)?.readyState === WebSocket.CONNECTING
    ) {
      console.log(
        `WebSocket is connecting to channel: ${this.namespace}/${channel}`
      );
      return;
    }

    try {
      this.updateConnectionStatus("connecting");

      const socket = new WebSocket(
        `wss://${this.appSyncDomain}/event/realtime`,
        ["aws-appsync-event-ws", this.getAuthProtocol()]
      );

      socket.onopen = () => {
        console.log(`WebSocket connected to channel: ${channel}`);
        this.updateConnectionStatus("connected");
        this.reconnectAttempts.set(channel, 0);
        this.subscribe(channel);
      };

      socket.onmessage = (event) => this.handleMessage(event);

      socket.onerror = (error) => {
        console.error(`WebSocket error on channel ${channel}:`, error);
        this.updateConnectionStatus("disconnected");
      };

      socket.onclose = () => {
        console.log(`WebSocket connection closed on channel: ${channel}`);
        this.updateConnectionStatus("disconnected");
        this.attemptReconnect(channel);
      };

      this.sockets.set(channel, socket);
    } catch (error) {
      console.error(`Failed to connect to channel ${channel}:`, error);
      this.updateConnectionStatus("disconnected");
      this.attemptReconnect(channel);
    }
  }

  sendMessage(channel: string, message: any): boolean {
    const socket = this.sockets.get(channel);

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.error(
        `Cannot send message: WebSocket not connected to channel ${channel}`
      );
      return false;
    }

    const time = new Date().toISOString();
    // this.addMessageToUI(message, time, true);

    socket.send(
      JSON.stringify({
        type: "publish",
        id: this.simulateUUID(),
        channel: `/${this.namespace}/${channel}`,
        events: [JSON.stringify(message)],
        authorization: {
          Authorization: `Bearer ${this.authService.getAuthorizationHeaderValue()}`,
          host: this.appSyncDomain,
        },
      })
    );

    return true;
  }

  protected handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);

      if (data.type === "data" && data.event) {
        const eventData = JSON.parse(data.event);
        console.log("Received message:", eventData);
        this.increaseNotificationsCount();
        this.addMessageToUI(eventData, eventData.time, false);
      }
    } catch (error) {
      console.error("Error processing message:", error);
    }
  }

  increaseNotificationsCount(count: number = 1) {
    this.countNotificationsSubject.next(count);
  }

  decreaseNotificationsCount(count: number = -1) {
    this.countNotificationsSubject.next(count);
  }

  setNotificationsCount(count: number) {
    this.setNotificationsCountSubject.next(count);
  }

  protected updateConnectionStatus(
    status: "connecting" | "connected" | "disconnected" | "failed"
  ): void {
    this.connectionStatusSubject.next(status);
  }

  protected formatTime(isoTime: string): string {
    try {
      if (isoTime.length === 8 && isoTime.includes(":")) {
        return isoTime;
      }
      const timePart = isoTime.includes("T") ? isoTime.split("T")[1] : isoTime;
      return timePart.slice(0, 8);
    } catch (e) {
      console.error("Error formatting time:", e);
      return new Date().toTimeString().slice(0, 8);
    }
  }

  protected addMessageToUI(eventData: T, time: string, isSent: boolean): void {
    // const currentMessages = this.messagesSubject.value;
    console.log("add to ui currentMessages: ", eventData);
    // this.messagesSubject.next([...currentMessages, eventData]);
    this.messagesSubject.next(eventData);
  }

  protected simulateUUID() {
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }

    return (
      s4() +
      s4() +
      "-" +
      s4() +
      "-" +
      s4() +
      "-" +
      s4() +
      "-" +
      s4() +
      s4() +
      s4()
    );
  }

  private getAuthProtocol(): string {
    const authorization = {
      Authorization: `Bearer ${this.authService.getAuthorizationHeaderValue()}`,
      host: this.appSyncDomain,
    };

    const header = btoa(JSON.stringify(authorization))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");

    return `header-${header}`;
  }

  private subscribe(channel: string): void {
    const socket = this.sockets.get(channel);
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.error(
        `Cannot subscribe: WebSocket not connected to channel ${channel}`
      );
      return;
    }

    console.log("subscribing to channel: ", channel);
    socket.send(
      JSON.stringify({
        type: "subscribe",
        id: this.simulateUUID(),
        channel: `/${this.namespace}/${channel}`,
        authorization: {
          Authorization: `Bearer ${this.authService.getAuthorizationHeaderValue()}`,
          host: this.appSyncDomain,
        },
      })
    );
  }

  private attemptReconnect(channel: string): void {
    if (this.reconnectAttempts.get(channel)) {
      const attempts = this.reconnectAttempts.get(channel) || 0;
      if (attempts < this.maxReconnectAttempts) {
        this.reconnectAttempts.set(channel, attempts + 1);
        const delay = this.reconnectDelay * Math.pow(1.5, attempts);

        setTimeout(() => this.connect(channel), delay);
      } else {
        this.updateConnectionStatus("failed");
      }
    }
  }

  clearMessages(): void {
    if (this.countNotificationsSubject.getValue() === 0) return;

    // Reset the messages
    this.messagesSubject.next({} as T);

    // Reset the notifications counts to zero
    this.countNotificationsSubject.next(0);
    this.setNotificationsCountSubject.next(0);
  }

  clearChatMessages(): void {
    if (this.countNotificationsSubject.getValue() === 0) return;

    // Reset the messages
    this.messagesSubject.next(null as T);

    // Reset the message counts to zero
    this.countNotificationsSubject.next(0);
    this.setNotificationsCountSubject.next(0);
  }

  unsubscribeAll(): void {
    this.sockets.forEach((socket, channel) => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(
          JSON.stringify({
            type: "unsubscribe",
            id: this.simulateUUID(),
            channel: `/${this.namespace}/${channel}`,
            authorization: {
              Authorization: `Bearer ${this.authService.getAuthorizationHeaderValue()}`,
              host: this.appSyncDomain,
            },
          })
        );
        socket.close();
      }
    });

    this.sockets.clear();
    this.reconnectAttempts.clear();
    this.updateConnectionStatus("disconnected");
    console.log("Unsubscribed from all channels and cleared connections.");
  }
}



// WEBPACK FOOTER //
// ./src/providers/appsync/base-websocket.service.ts